import type { FC } from 'react'
import type { MenuCategory, MenuItem } from '@/types'

import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatPrice } from '@/lib/format'
import { Photo } from '@/components/generals/photo'
import { QuantityStepper } from '@/components/generals/quantity-stepper'

interface OrderItemListProps {
    categories: Array<MenuCategory>
    items: Array<MenuItem>
    quantities: Map<string, number>
    onChange: (item: MenuItem, quantity: number) => void
}

const availabilityText = (item: MenuItem): string | null => {
    if (item.status === 'sold-out') return 'امروز تمام شد'
    if (item.status === 'selling-fast') return 'امروز رو به اتمام'
    if (!item.status) return 'امروز پخته نمی‌شود'

    return null
}

export const OrderItemList: FC<OrderItemListProps> = ({ categories, items, quantities, onChange }) => {
    const groups = categories
        .map(category => ({ category, items: items.filter(item => item.categorySlug === category.slug) }))
        .filter(group => group.items.length > 0)

    return (
        <div className='space-y-14'>
            <nav aria-label='رفتن به یک دسته' className='flex flex-wrap gap-2'>
                {groups.map(group => (
                    <a
                        key={group.category.slug}
                        href={`#order-${group.category.slug}`}
                        className='inline-flex h-9 items-center border border-line-strong px-4 text-sm transition-colors duration-300 hover:border-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber'
                    >
                        {group.category.id ? <span data-mdlfy-cms={`menu_categories:${group.category.id}:name`}>{group.category.name}</span> : group.category.name}
                    </a>
                ))}
            </nav>

            {groups.map(group => (
                <section key={group.category.slug} id={`order-${group.category.slug}`} aria-labelledby={`order-${group.category.slug}-title`}>
                    <h2 id={`order-${group.category.slug}-title`} className='font-heading text-[2.25rem] leading-none font-normal'>
                        {group.category.id ? <span data-mdlfy-cms={`menu_categories:${group.category.id}:name`}>{group.category.name}</span> : group.category.name}
                    </h2>

                    <ul className='mt-5 border-t border-line'>
                        {group.items.map(item => {
                            const quantity = quantities.get(item.slug) ?? 0
                            const note = availabilityText(item)

                            return (
                                <li key={item.id} className={cn('grid grid-cols-[3.75rem_minmax(0,1fr)_auto] items-center gap-x-4 border-b border-line py-4 sm:grid-cols-[4.5rem_minmax(0,1fr)_auto] sm:gap-x-5', quantity > 0 && 'bg-flour-deep/40')}>
                                    <Photo
                                        src={item.image}
                                        alt={item.imageAlt}
                                        sizes='72px'
                                        reveal={false}
                                        fallback={item.name.charAt(0)}
                                        className='aspect-square'
                                    />

                                    <div className='min-w-0'>
                                        <h3 className='font-heading text-[1.35rem] leading-tight font-normal sm:text-2xl' data-mdlfy-cms={`menu_items:${item.id}:name`}>{item.name}</h3>

                                        <p className='mt-0.5 text-sm text-crust-soft'>
                                            <span className='text-foreground tabular-nums'>{formatPrice(item.price)}</span>
                                            {note && <span> · {note}</span>}
                                        </p>
                                    </div>

                                    {quantity === 0 ? (
                                        <button
                                            type='button'
                                            onClick={() => onChange(item, 1)}
                                            aria-label={`افزودن ${item.name} به سبد`}
                                            className='inline-flex h-11 cursor-pointer items-center gap-1.5 rounded-sm border border-line-strong px-4 text-sm font-medium transition-colors duration-300 hover:border-foreground hover:bg-foreground hover:text-background focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber'
                                        >
                                            <Plus className='size-4' aria-hidden='true' />
                                            افزودن
                                        </button>
                                    ) : (
                                        <QuantityStepper value={quantity} onChange={next => onChange(item, next)} label={item.name} />
                                    )}
                                </li>
                            )
                        })}
                    </ul>
                </section>
            ))}
        </div>
    )
}
